/* RVP with QC › Courier QC Master — 3-step path (lands on Review / Step 3).
   UI reproduces the v5 mockup (rvpQc.css). Data wiring unchanged:
   Step 1 = live qc-parameter-master; Step 2 courier tabs = cached courier API
   (qcType doorstep, by parent), mapping options = courier-qc-questions/by-partner. */
import React, { useEffect, useMemo, useState } from 'react'
import { Box, Popover, Alert, Chip, Tooltip, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import PageTitle from '../../../components/PageTitle'
import ProzoDataGrid from '../../../common/ProzoDataGrid'
import { CellLine, CodeCell, humanize, PillStepper, SearchSelect, gridSx } from './gridCells'
import {
  useQcParameterMasterQuery, useCreateParameterMutation, useUpdateParameterMutation,
  useAddCpMapMutation, useRemoveCpMapMutation, useCourierQcQuestionsQuery,
} from '../../../redux/rvpQcApiSlice'
import { useGetAllCourierPartnersCachedQuery } from '../../../redux/commonApiCalls'
import './rvpQc.css'

const STEPS = [{ n: 1, t: 'Proship Master QC Parameters' }, { n: 2, t: 'Courier QC Parameters' }, { n: 3, t: 'Review' }]

const RVP_QC_COURIER_PAYLOAD = {
  andfilter: [{ field: 'is_deleted', operator: 'eq', value: '0' }],
  offset: 0, limit: 1000, skipCount: true,
  includes: 'id,name,parent,cpAccountCode,prozoId,qcType,type,cp_id,serviceTypePrimary',
}

// ── Shared cell chrome so these tables read the same as the premium Forward-Shipment table:
// compact, boxy chips (6px radius) with meaningful colour rather than flat text.
const chipBaseSx = { height: 22, fontSize: 11, fontWeight: 600, borderRadius: '6px', '& .MuiChip-label': { px: 0.9 } }
const monoChipSx = { ...chipBaseSx, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 11.5, letterSpacing: '-0.01em' }
// A courier param's input type gets a colour by kind — image/text/number/choice/boolean — so the
// metadata columns have visual rhythm instead of a wall of grey text.
const INPUT_TYPE_COLORS = {
  IMAGE: 'info', IMAGE_UPLOAD: 'info', PHOTO: 'info', FILE: 'info',
  MULTI: 'info', MULTISELECT: 'info', MULTI_SELECT: 'info',
  NUMBER: 'secondary', NUMERIC: 'secondary', INTEGER: 'secondary',
  DROPDOWN: 'warning', SELECT: 'warning', ENUM: 'warning', SINGLE: 'warning',
  BOOLEAN: 'success', CHECKBOX: 'success', YES_NO: 'success', TOGGLE: 'success',
  VARCHAR: 'default', TEXT: 'default', STRING: 'default', TEXTBOX: 'default',
}
function InputTypeChip({ value }) {
  if (!value) return <CellLine muted>—</CellLine>
  const color = INPUT_TYPE_COLORS[String(value).toUpperCase()] || 'default'
  return <Chip size="small" variant="outlined" color={color} label={humanize(value)} sx={chipBaseSx} />
}

export default function RvpQcMaster() {
  const { data: realParamsRaw, isFetching: paramsLoading, isError: paramsError } = useQcParameterMasterQuery({ activeOnly: true })
  const realParams = Array.isArray(realParamsRaw) ? realParamsRaw : (Array.isArray(realParamsRaw?.data) ? realParamsRaw.data : [])
  const [createParameter, { isLoading: creating }] = useCreateParameterMutation()
  const [updateParameter, { isLoading: updating }] = useUpdateParameterMutation()
  const [addCpMap] = useAddCpMapMutation()
  const [removeCpMap] = useRemoveCpMapMutation()
  const { data: courierRows = [], isFetching: couriersLoading } = useGetAllCourierPartnersCachedQuery(RVP_QC_COURIER_PAYLOAD)

  const [step, setStep] = useState(1)
  const [courier, setCourier] = useState('')
  const [mpDialog, setMpDialog] = useState(null)
  const [mpErr, setMpErr] = useState('')
  const [confirm, setConfirm] = useState(null)

  const { activeCouriers, parentCpId } = useMemo(() => {
    const cpMap = {}
    const parents = []
    ;(courierRows || [])
      .filter((c) => (c?.serviceTypePrimary === "REV_REG_QC") && c?.parent)
      .forEach((c) => {
        if (!parents.includes(c.parent)) parents.push(c.parent)
        // take the first row for this parent that ACTUALLY has a cp id (not just the first row)
        const id = c?.cpId ?? c?.cp_id
        if (id != null && cpMap[c.parent] == null) cpMap[c.parent] = id
      })
    parents.sort((a, b) => String(a).localeCompare(String(b)))
    return { activeCouriers: parents, parentCpId: cpMap }
  }, [courierRows])

  const selectedCpId = parentCpId[courier]
  const { data: courierQc = [], isFetching: qcLoading, isError: qcError } =
    useCourierQcQuestionsQuery(selectedCpId, { skip: selectedCpId == null })

  useEffect(() => {
    if (activeCouriers.length && !activeCouriers.includes(courier)) setCourier(activeCouriers[0])
    if (!activeCouriers.length && courier) setCourier('')
  }, [activeCouriers, courier])

  // persist a mapping: pick → §2.5 upsert cp-map; clear → §2.6 remove. Refetch §2.1 (Params tag)
  // updates each param's cpMap, which drives Step 2's current value and Step 3's coverage.
  const onMap = async (paramId, code) => {
    if (selectedCpId == null) return
    try {
      if (code) await addCpMap({ paramId, cpId: selectedCpId, cpQcCode: code }).unwrap()
      else await removeCpMap({ paramId, cpId: selectedCpId }).unwrap()
    } catch (e) { window.alert(e?.message || 'Could not update the mapping.') }
  }

  const onSaveMp = async ({ code, desc }) => {
    const c = (code || '').trim()
    if (!c) { setMpErr('Enter a code.'); return }
    try {
      if (mpDialog.mode === 'add') await createParameter({ code: c, displayName: c, description: (desc || '').trim() }).unwrap()
      else await updateParameter({ id: mpDialog.id, body: { code: c, displayName: c, description: (desc || '').trim() } }).unwrap()
      setMpDialog(null); setMpErr('')
    } catch (e) { setMpErr(e?.message || 'Could not save parameter.') }
  }
  const askDeleteMp = (param) => {
    const used = (param.cpMap || []).length
    const extra = []
    if (used) extra.push(`${used} courier mapping${used === 1 ? '' : 's'} will be removed`)
    extra.push('it will be removed from any merchant setups that selected it')
    setConfirm({ kind: 'delmp', id: param.id, confirmLabel: 'Delete parameter',
      msg: `Delete master parameter “${param.code}”? ${extra.join(', and ')}.` })
  }
  const onConfirm = async () => {
    const cb = confirm; setConfirm(null)
    if (cb?.kind === 'delmp') { try { await updateParameter({ id: cb.id, body: { isActive: false } }).unwrap() } catch (e) { window.alert(e?.message || 'Action failed.') } }
  }

  return (
    <div className="rvpqc">
      <PageTitle>RVP with QC · Courier QC Master</PageTitle>

      <PillStepper steps={STEPS} current={step} onChange={setStep} />

      {step === 1 && (
        <Step1 params={realParams} loading={paramsLoading} isError={paramsError}
          onAdd={() => { setMpErr(''); setMpDialog({ mode: 'add', code: '', desc: '' }) }}
          onEdit={(p) => { setMpErr(''); setMpDialog({ mode: 'edit', id: p.id, code: p.code, desc: p.description || '' }) }}
          onDelete={askDeleteMp} onNext={() => setStep(2)} />
      )}

      {step === 2 && (
        <Step2 params={realParams} activeCouriers={activeCouriers} couriersLoading={couriersLoading} courier={courier}
          setCourier={setCourier} courierQc={courierQc} qcLoading={qcLoading} qcError={qcError}
          noCpId={!!courier && selectedCpId == null} cpId={selectedCpId}
          onMap={onMap} onBack={() => setStep(1)} onNext={() => setStep(3)} />
      )}

      {step === 3 && (
        <Step3 params={realParams} activeCouriers={activeCouriers} couriersLoading={couriersLoading} courier={courier}
          setCourier={setCourier} parentCpId={parentCpId} />
      )}

      {mpDialog && <MasterParamModal data={mpDialog} error={mpErr} busy={creating || updating}
        onClose={() => { setMpDialog(null); setMpErr('') }} onSave={onSaveMp} />}
      {confirm && <ConfirmModal data={confirm} onClose={() => setConfirm(null)} onConfirm={onConfirm} />}
    </div>
  )
}

/* ════════ Step 1 ════════ */
function Step1({ params, loading, isError, onAdd, onEdit, onDelete, onNext }) {
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(20)
  // "Mapped to" click opens a popover instead of expanding the row in place — the free
  // MUI X DataGrid (this repo's version) doesn't support Pro's expandable detail rows.
  const [popoverAnchor, setPopoverAnchor] = useState(null)
  const [popoverParam, setPopoverParam] = useState(null)
  const openMappedTo = (e, p) => { setPopoverAnchor(e.currentTarget); setPopoverParam(p) }
  const closeMappedTo = () => { setPopoverAnchor(null); setPopoverParam(null) }

  const columns = [
    { field: 'code', headerName: 'Proship Master QC Parameter', flex: 1.6, minWidth: 260,
      renderCell: (params_) => <CodeCell code={params_.row.code} description={params_.row.description} /> },
    { field: 'mapped', headerName: 'Mapped to', width: 160, sortable: false, align: 'center', headerAlign: 'center',
      renderCell: (params_) => {
        const cp = params_.row.cpMap || []
        return cp.length
          ? <Chip icon={<CheckCircleIcon />} label={`${cp.length} courier${cp.length === 1 ? '' : 's'}`} size="small" color="success" variant="outlined" onClick={(e) => openMappedTo(e, params_.row)} sx={{ ...chipBaseSx, cursor: 'pointer', '& .MuiChip-icon': { fontSize: 14, ml: 0.5 } }} />
          : <Chip label="Not mapped" size="small" variant="outlined" sx={{ ...chipBaseSx, color: 'text.disabled', borderColor: '#E2E8F0' }} />
      } },
    { field: 'actions', headerName: 'Actions', width: 140, sortable: false, align: 'right', headerAlign: 'right',
      renderCell: (params_) => (
        <>
          <button className="linkbtn" onClick={() => onEdit(params_.row)}>Edit</button>
          <button className="linkbtn del" onClick={() => onDelete(params_.row)}>Delete</button>
        </>
      ) },
  ]

  return (
    <>
      <div className="card">
        <div className="row" style={{ marginBottom: 10 }}>
          <h2 style={{ margin: 0 }}>Step 1 · Proship Master QC Parameters</h2>
          <Tooltip title="Proship Master QC Parameters are constant. They are the courier-independent vocabulary for QC. Each courier’s own QC parameters are mapped against these in Step 2, and merchants pick checks by these codes. Create them carefully — duplicate codes are not allowed.">
            <InfoOutlinedIcon sx={{ fontSize: 17, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
          <div className="spacer" />
          <span className="counter"><b>{params.length}</b> parameters</span>
          <button className="btn" style={{ marginLeft: 12 }} onClick={onAdd}>+ Add master parameter</button>
        </div>
        <div className="ctx">The constant list Proship understands. Add a parameter here before any courier can map to
          it. This same list is what merchant QC setups choose from. <b>Click "Mapped to"</b> to see which courier
          partners it is mapped to.</div>
        {loading && <div className="ctx">Loading parameters…</div>}
        {isError && <Alert severity="warning" sx={{ mb: 2 }}>Failed to load QC parameter master.</Alert>}
        {!loading && !params.length ? (
          <div className="empty"><h3>No master parameters yet</h3><p>Add your first constant parameter to begin.</p></div>
        ) : (
          <ProzoDataGrid
            columns={columns}
            rows={params}
            getRowId={(row) => row.id}
            rowCount={params.length}
            pagination
            paginationMode="client"
            page={page}
            setPage={setPage}
            sizePerPage={sizePerPage}
            setSizePerPage={setSizePerPage}
            getRowHeight={() => 'auto'}
            flat={false}
            sx={gridSx}
          />
        )}
      </div>
      <div className="sticky-actions"><button className="btn" onClick={onNext}>Next: Courier mapping →</button></div>

      <Popover open={!!popoverAnchor} anchorEl={popoverAnchor} onClose={closeMappedTo}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ sx: { mt: 0.75, borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: '0 10px 30px rgba(15,23,42,0.16)', overflow: 'hidden' } }}>
        <Box sx={{ minWidth: 250, maxWidth: 340 }}>
          {/* header */}
          <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>Mapped couriers</Typography>
            {(popoverParam?.cpMap || []).length > 0 && (
              <Chip size="small" label={popoverParam.cpMap.length} color="primary" sx={{ height: 20, fontWeight: 700, '& .MuiChip-label': { px: 1 } }} />
            )}
          </Box>
          {/* body */}
          {(popoverParam?.cpMap || []).length ? (
            <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {popoverParam.cpMap.map((m) => (
                <Box key={m.cpId} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: 1, py: 0.75, borderRadius: 1.5, '&:hover': { bgcolor: 'action.hover' } }}>
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Courier&nbsp;partner&nbsp;#{m.cpId}</Typography>
                  <Chip size="small" label={m.cpQcCode} variant="outlined" color="primary"
                    sx={{ height: 22, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11.5, fontWeight: 600 }} />
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>Not mapped to any courier partner yet — map it in Step 2.</Typography>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  )
}

// Individual rounded segmented buttons (not connected) — each courier is a white pill with a small
// truck glyph; the active one fills blue. Reads as a proper selector rather than loose chips.
const courierToggleSx = {
  flexWrap: 'wrap', gap: 0.75,
  '& .MuiToggleButtonGroup-grouped': {
    m: 0, border: '1px solid #D0D7DE', borderRadius: '8px !important',
    textTransform: 'none', fontSize: 12.5, fontWeight: 600, color: '#475569',
    px: 1.5, py: 0.5, bgcolor: '#fff', lineHeight: 1.4,
    '&:not(:first-of-type)': { borderLeft: '1px solid #D0D7DE', ml: 0 },
    '&:hover': { bgcolor: '#EAF2FC', borderColor: '#BFD7F5' },
    '&.Mui-selected': { bgcolor: 'primary.main', color: '#fff', borderColor: 'primary.main', boxShadow: '0 2px 6px rgba(32,101,209,.25)' },
    '&.Mui-selected:hover': { bgcolor: 'primary.dark', borderColor: 'primary.dark' },
  },
}
/* shared courier selector bar — a contained toolbar with a segmented button group so it reads as a
   distinct control group instead of chips floating on the white card. */
function CourierBar({ activeCouriers, courier, setCourier, loading }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2, py: 1.25, px: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: '10px' }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Courier partner</Typography>
      {loading ? (
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Loading couriers…</Typography>
      ) : activeCouriers.length ? (
        <ToggleButtonGroup exclusive size="small" value={courier} onChange={(e, v) => { if (v) setCourier(v) }} sx={courierToggleSx}>
          {activeCouriers.map((c) => (
            <ToggleButton key={c} value={c} disableRipple>
              <LocalShippingOutlinedIcon sx={{ fontSize: 15, mr: 0.75, opacity: 0.75 }} />{c}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      ) : (
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>No RVP-QC couriers</Typography>
      )}
    </Box>
  )
}

/* ════════ Step 2 ════════ */
function Step2({ params, activeCouriers, couriersLoading, courier, setCourier, courierQc, qcLoading, qcError, noCpId, cpId, onMap, onBack, onNext }) {
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(20)
  const byCode = new Map((courierQc || []).map((x) => [x.qcParameterCode, x]))
  // current mapping for this courier is read from each param's saved cpMap (cpQcCode = qcParameterCode)
  const curOf = (p) => (p.cpMap || []).find((m) => String(m.cpId) === String(cpId))?.cpQcCode || ''
  const mappedCount = params.filter((p) => curOf(p)).length
  const dashCell = <CellLine muted>—</CellLine>
  const columns = [
    { field: 'code', headerName: 'Proship Master QC Parameter', flex: 1.4, minWidth: 240,
      renderCell: (params_) => <CodeCell code={params_.row.code} description={params_.row.description} /> },
    { field: 'mapping', headerName: `${courier} QC Parameter`, width: 240, sortable: false,
      renderCell: (params_) => {
        const p = params_.row
        const cur = curOf(p)
        const usedByOther = new Set(params.filter((o) => o.id !== p.id).map(curOf).filter(Boolean))
        const opts = (courierQc || []).filter((x) => !usedByOther.has(x.qcParameterCode))
        return (
          <SearchSelect
            options={opts}
            value={opts.find((x) => x.qcParameterCode === cur) || null}
            disabled={!courierQc?.length}
            showSearchIcon={false}
            placeholder="— Not mapped —"
            noOptionsText="No courier parameters match"
            getOptionLabel={(x) => humanize(x.qcParameterCode)}
            getOptionKey={(x) => x.qcParameterCode}
            isOptionEqualToValue={(a, b) => a.qcParameterCode === b.qcParameterCode}
            filterText={(x) => `${humanize(x.qcParameterCode)} ${x.qcParameterCode}`}
            onChange={(x) => onMap(p.id, x ? x.qcParameterCode : '')}
            sx={{ width: '100%' }}
          />
        )
      } },
    { field: 'apiField', headerName: 'API field', width: 160, sortable: false,
      renderCell: (params_) => {
        const item = curOf(params_.row) ? byCode.get(curOf(params_.row)) : null
        if (!item) return dashCell
        return item.apiField
          ? <Chip size="small" variant="outlined" label={item.apiField} sx={{ ...monoChipSx, color: 'text.secondary', borderColor: '#E2E8F0' }} />
          : <CellLine muted>shipment-level</CellLine>
      } },
    { field: 'inputType', headerName: 'Input type', width: 130, sortable: false,
      renderCell: (params_) => {
        const item = curOf(params_.row) ? byCode.get(curOf(params_.row)) : null
        return item ? <InputTypeChip value={item.inputType} /> : dashCell
      } },
    { field: 'questionJson', headerName: 'Question (JSON)', flex: 1, minWidth: 240, sortable: false,
      renderCell: (params_) => {
        const item = curOf(params_.row) ? byCode.get(curOf(params_.row)) : null
        return item && item.questionJson ? <CellLine muted ellipsis>{item.questionJson}</CellLine> : dashCell
      } },
  ]

  return (
    <>
      {/* Was a full warning banner — folded into a subtle one-liner; the full detail lives in the ⓘ. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5, color: 'text.secondary' }}>
        <InfoOutlinedIcon sx={{ fontSize: 15, flexShrink: 0 }} />
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          Courier QC parameters are managed in <b>PCIS</b> — you only map them here. To add or change them, email{' '}
          <a href="mailto:techteam@prozo.com">techteam@prozo.com</a>.
        </Typography>
        <Tooltip title="Courier QC parameters are added in PCIS (Prozo Courier Integration System), not on this screen. To add or change a courier’s QC parameters, email techteam@prozo.com or contact the tech team. Here you only map the parameters that flow in from PCIS to your Proship master parameters.">
          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
        </Tooltip>
      </Box>

      <div className="card">
        <div className="row">
          <h2 style={{ margin: 0 }}>Step 2 · Map courier QC parameters</h2>
          <Tooltip title="Pick a courier, then map each ProShip Master Parameter to one of that courier’s QC parameters. Mapping is one-to-one — choosing a parameter auto-fills its API field, input type and question.">
            <InfoOutlinedIcon sx={{ fontSize: 17, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </div>
        <div style={{ height: 10 }} />
        <CourierBar activeCouriers={activeCouriers} courier={courier} setCourier={setCourier} loading={couriersLoading} />
        <div className="ctx">{courier
          ? <span><b>{courier}</b> maps <b>{mappedCount}</b> of {params.length} Proship Master QC Parameters. Parameters flow in from PCIS — pick one per master below.</span>
          : 'Select a courier partner to begin mapping.'}</div>
        {noCpId && <Alert severity="warning" sx={{ mb: 2 }}>No courier id (cp_id) is available for <b>{courier}</b>, so its QC parameters can’t be loaded.</Alert>}
        {qcLoading && <div className="ctx">Loading {courier}’s QC parameters…</div>}
        {qcError && <Alert severity="warning" sx={{ mb: 2 }}>Failed to load {courier}’s QC parameters.</Alert>}

        {courier && (
          !courierQc?.length && !qcLoading ? (
            <div className="empty"><h3>No QC parameters for {courier}</h3></div>
          ) : (
            <ProzoDataGrid
              columns={columns}
              rows={params}
              getRowId={(row) => row.id}
              rowCount={params.length}
              pagination
              paginationMode="client"
              page={page}
              setPage={setPage}
              sizePerPage={sizePerPage}
              setSizePerPage={setSizePerPage}
              getRowHeight={() => 'auto'}
              flat={false}
              sx={gridSx}
            />
          )
        )}
      </div>
      <div className="sticky-actions">
        <button className="btn muted" onClick={onBack}>← Back</button>
        <button className="btn" onClick={onNext}>Next: Review →</button>
      </div>
    </>
  )
}

/* ════════ Step 3 ════════ */
/* Coverage is derived from each master param's cpMap: a courier "supports" a param when the
   courier's cp_id appears in that param's cpMap (with its cpQcCode). */
function Step3({ params, activeCouriers, couriersLoading, courier, setCourier, parentCpId }) {
  const [page1, setPage1] = useState(0)
  const [sizePerPage1, setSizePerPage1] = useState(20)
  const [page2, setPage2] = useState(0)
  const [sizePerPage2, setSizePerPage2] = useState(20)
  const cqcFor = (p, cpId) => (p.cpMap || []).find((m) => String(m.cpId) === String(cpId))?.cpQcCode
  const selectedCpId = parentCpId[courier]
  const mapped = params.filter((p) => cqcFor(p, selectedCpId))

  const coverageColumns = [
    { field: 'code', headerName: 'Proship Master QC Parameter', minWidth: 260, flex: 1.4,
      renderCell: (params_) => <CodeCell code={params_.row.code} description={params_.row.description} /> },
    ...activeCouriers.map((c) => ({
      field: c, headerName: c, width: 130, sortable: false, align: 'center', headerAlign: 'center',
      renderCell: (params_) => {
        const cqc = cqcFor(params_.row, parentCpId[c])
        return cqc
          ? <CheckCircleIcon titleAccess={`${c}: ${cqc}`} sx={{ fontSize: 19, color: 'success.main' }} />
          : <Box component="span" sx={{ color: 'grey.300', fontWeight: 700 }}>—</Box>
      },
    })),
  ]

  const detailColumns = [
    { field: 'code', headerName: 'Proship Master QC Parameter', flex: 1.4, minWidth: 240,
      renderCell: (params_) => <CodeCell code={params_.row.code} description={params_.row.description} /> },
    { field: 'cqc', headerName: `${courier} QC Parameter`, flex: 1, minWidth: 200, sortable: false,
      renderCell: (params_) => {
        const v = cqcFor(params_.row, selectedCpId)
        return v ? <Chip size="small" variant="outlined" color="primary" title={v} label={humanize(v)} sx={chipBaseSx} /> : <CellLine muted>—</CellLine>
      } },
  ]

  return (
    <>
      <div className="card">
        <div className="row" style={{ marginBottom: 8 }}>
          <h2 style={{ margin: 0 }}>Coverage overview</h2>
          <Tooltip title="Read-only preview. A compilation of Step 1 (your master parameters) and Step 2 (how each courier’s QC parameters map to them). To make changes, go back to Step 1 or Step 2.">
            <InfoOutlinedIcon sx={{ fontSize: 17, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
          <div className="spacer" />
          <span className="counter"><b>{activeCouriers.length}</b> couriers · <b>{params.length}</b> master params</span>
        </div>
        <div className="ctx">Which master parameter each courier supports. A green ✓ means the courier maps it — hover to
          see that courier’s QC parameter name. Scrolls sideways as you add couriers.</div>
        {activeCouriers.length ? (
          <ProzoDataGrid
            columns={coverageColumns}
            rows={params}
            getRowId={(row) => row.id}
            rowCount={params.length}
            pagination
            paginationMode="client"
            page={page1}
            setPage={setPage1}
            sizePerPage={sizePerPage1}
            setSizePerPage={setSizePerPage1}
            getRowHeight={() => 'auto'}
            flat={false}
            sx={gridSx}
          />
        ) : <div className="empty"><h3>No courier partners</h3></div>}
      </div>

      <div className="card">
        <h2>Per-courier detail{courier ? ` — ${courier}` : ''}</h2>
        <div style={{ height: 10 }} />
        <CourierBar activeCouriers={activeCouriers} courier={courier} setCourier={setCourier} loading={couriersLoading} />
        {courier && mapped.length ? (
          <ProzoDataGrid
            columns={detailColumns}
            rows={mapped}
            getRowId={(row) => row.id}
            rowCount={mapped.length}
            pagination
            paginationMode="client"
            page={page2}
            setPage={setPage2}
            sizePerPage={sizePerPage2}
            setSizePerPage={setSizePerPage2}
            getRowHeight={() => 'auto'}
            flat={false}
            sx={gridSx}
          />
        ) : <div className="empty"><h3>{courier ? `No mappings for ${courier}` : 'No courier partners'}</h3>{courier && <p>Map this courier’s QC parameters in Step 2.</p>}</div>}
      </div>
    </>
  )
}

/* ════════ modals ════════ */
function MasterParamModal({ data, error, busy, onClose, onSave }) {
  const [code, setCode] = useState(data.code || '')
  const [desc, setDesc] = useState(data.desc || '')
  const edit = data.mode === 'edit'
  return (
    <div className="rvpqc-modal-ov" onClick={onClose}>
      <div className="rvpqc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mhd">
          <div className="ic">+</div>
          <div><h3>{edit ? 'Edit master parameter' : 'Add ProShip master parameter'}</h3>
            <div className="sub">Constant &amp; courier-independent — used across every courier and merchant.</div></div>
          <button className="x" onClick={onClose} title="Close">×</button>
        </div>
        <div className="mbd">
          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
          <div className="mfield">
            <label>Parameter code <span className="req">*</span></label>
            <input type="text" className="mono-in" value={code} autoFocus placeholder="e.g. SIZE / BRAND / CHECK_DAMAGED" onChange={(e) => setCode(e.target.value)} />
            <div className="mhint">Courier-agnostic code. Upper-cased automatically; must be unique.</div>
          </div>
          <div className="mfield">
            <label>Description</label>
            <textarea value={desc} style={{ fontFamily: 'inherit' }} onChange={(e) => setDesc(e.target.value)} />
            <div className="mhint">Optional plain-language note about what this check verifies.</div>
          </div>
          {edit && <Alert severity="warning" sx={{ mt: 1.5 }}>Renaming updates this parameter everywhere it is mapped and selected. Existing courier mappings stay attached.</Alert>}
        </div>
        <div className="mft">
          <button className="btn muted" onClick={onClose}>Cancel</button>
          <button className="btn" disabled={busy} onClick={() => onSave({ code, desc })}>{busy ? 'Saving…' : edit ? 'Save changes' : 'Add master parameter'}</button>
        </div>
      </div>
    </div>
  )
}

function ConfirmModal({ data, onClose, onConfirm }) {
  return (
    <div className="rvpqc-modal-ov" onClick={onClose}>
      <div className="rvpqc-modal" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
        <div className="mhd">
          <div className="ic" style={{ background: 'linear-gradient(135deg,#DC2626,#B78103)' }}>!</div>
          <div><h3>Please confirm</h3></div>
          <button className="x" onClick={onClose} title="Close">×</button>
        </div>
        <div className="mbd">{data.msg}</div>
        <div className="mft">
          <button className="btn muted" onClick={onClose}>Cancel</button>
          <button className="btn danger" onClick={onConfirm}>{data.confirmLabel || 'Delete'}</button>
        </div>
      </div>
    </div>
  )
}
